import {
  compose,
  lifecycle,
  withHandlers,
  withProps,
  withState,
} from 'recompose';
import { parse } from 'query-string';
import { Form } from './Form';
import { actions } from '../../redux/modules/submission';
import { connect } from '../../redux/store';
import { Utils, C, addToast } from '@kineticdata/bundle-common';

const valuesFromQueryParams = queryParams => {
  const params = parse(queryParams);
  return Object.entries(params).reduce((values, [key, value]) => {
    if (key.startsWith('values[')) {
      const vk = key.match(/values\[(.*?)\]/)[1];
      return { ...values, [vk]: value };
    }
    return values;
  }, {});
};

export const handleCompleted = props => response => {
  if (props.authenticated) {
    // Check if either currentPage is null (pre form consolidation) or
    // displayedPage.type is not 'confirmation' (post form-consolidation)
    // to determine that there is no confirmation page and we should redirect.
    if (
      !response.submission.currentPage ||
      (response.submission.displayedPage &&
        response.submission.displayedPage.type !== 'confirmation')
    ) {
      props.navigate(
        `${props.appLocation}/requests/request/${
          response.submission.id
        }/confirmation`,
      );
    } else {
      props.setPage(response.submission.currentPage);
    }
  }
};

export const handleCreated = props => response => {
  if (
    response.submission.coreState !== 'Submitted' &&
    props.page === response.submission.currentPage
  ) {
    addToast('Form saved successfully');
  } else {
    props.setPage(response.submission.currentPage);
  }
  // Redirect to route with submission id if submission is not submitted or
  // there is a confirmation page to render, defined as currentPage is set and
  // displayedPage is undefined (pre form consolidation) or displayedPage.type
  // is 'confirmation' (post form-consolidation).
  if (
    response.submission.coreState !== 'Submitted' ||
    (response.submission.currentPage &&
      (!response.submission.displayedPage ||
        response.submission.displayedPage.type === 'confirmation'))
  ) {
    props.navigate(response.submission.id);
  }
};

export const handleUpdated = props => response => {
  if (
    response.submission.coreState !== 'Submitted' &&
    props.page === response.submission.currentPage
  ) {
    addToast('Form saved successfully');
  } else {
    props.setPage(response.submission.currentPage);
  }
};

export const handleUnauthorized = props => response => {
  if (!props.authenticated) {
    props.navigate(props.authRoute);
  }
};

export const handleLoaded = props => form => {
  props.setForm({
    slug: form.slug(),
    name: form.name(),
    type: form.type(),
    description: form.description(),
    image:
      form.attributes(C.ATTR_IMAGE) ||
      form.kapp().attributes(C.ATTRIBUTE_KAPP_DEFAULT_FORM_IMAGE),
    maintenance: form.attributes(C.FORM_ATTR_MAINTENANCE),
  });
  props.setPage(form.page().name());
};

export const handleDelete = props => () => {
  const deleteCallback = () => {
    props.navigate(props.appLocation);
  };
  props.deleteSubmission({ id: props.submissionId, success: deleteCallback });
};

export const mapStateToProps = (state, props) => {
  const requestMatch = props.location.pathname.match(
    /requests(\/([A-Za-z]+))?\/request/,
  );
  const categoryHelper = state.app.kapp.categoryHelper;
  const category =
    categoryHelper && props.categorySlug
      ? state.app.kapp.categoryHelper.getCategory(props.categorySlug)
      : null;
  const favorites = Utils.getProfileAttributeValues(
    state.app.profile,
    C.ATTRIBUTE_USER_PROFILE_FAVORITES,
  );
  return {
    category,
    values: valuesFromQueryParams(state.router.location.search),
    kappSlug: state.app.kappSlug,
    appLocation: state.app.location,
    authenticated: state.app.authenticated,
    authRoute: state.app.authRoute,
    rootLabel: !!requestMatch
      ? `${requestMatch[2] ? `${requestMatch[2]} ` : ''} Requests`
      : 'Catalog',
    rootPath: !!requestMatch
      ? `${state.app.location}/requests${
          requestMatch[2] ? `/${requestMatch[2]}` : ''
        }`
      : `${state.app.location}`,
    favorite: favorites && favorites.includes(props.formSlug),
    toggleFavorite: state.app.actions.toggleFavorite,
  };
};

export const mapDispatchToProps = {
  deleteSubmission: actions.deleteSubmissionRequest,
};

const enhance = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withProps(props => ({
    frameless: props.location.pathname.includes('frameless'),
  })),
  withState('form', 'setForm', props => props.form),
  withState('page', 'setPage', ''),
  withHandlers({
    handleCompleted,
    handleCreated,
    handleUpdated,
    handleLoaded,
    handleDelete,
    handleUnauthorized,
    handleToggleFavorite: ({ formSlug, toggleFavorite }) =>
      typeof toggleFavorite === 'function'
        ? () => toggleFavorite(formSlug)
        : undefined,
  }),
  lifecycle({
    componentDidUpdate(prevProps) {
      if (
        this.props.form &&
        this.props.form.type === C.PORTAL_PAGE_FORM_TYPE &&
        (!prevProps.form || prevProps.form.type !== this.props.form.type)
      ) {
        this.props.navigate(
          `${this.props.appLocation}/page/${this.props.formSlug ||
            this.props.form.slug}`,
          { replace: true },
        );
      }
    },
  }),
);

export const FormContainer = enhance(Form);
